/**
 * LoginPage — Anmeldung & Registrierung für den KI Social Media Manager.
 *
 * Nutzt die vorhandenen Auth-Helfer (login/register). Nach Erfolg wird der Nutzer
 * zum Ziel (?next=) bzw. zur Übersicht geleitet. Die Seite ist öffentlich (außerhalb
 * des ProtectedRoute-Gates) und das Tor zum SaaS-Produkt.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, Building2, ArrowRight, ShieldCheck, Server } from "lucide-react";
import { login, register, requestPasswordReset } from "@/lib/authToken";
import { trackEvent } from "@/lib/trackEvent";
import { getVariant } from "@/lib/ab_utils";
import { startTrialIfAbsent, usePlanSelection } from "@/hooks/usePlanSelection";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useLanguage();
  const rawNext = params.get("next") || "/";
  const next = rawNext.startsWith("/") ? rawNext : "/";
  // Kaufabsicht aus der Preisseite (?plan=creator): nach der Registrierung direkt in
  // den Checkout fortsetzen, statt sie am heißesten Funnel-Punkt zu verlieren.
  const intendedPlan = params.get("plan");
  const { selectPlan } = usePlanSelection();

  // Register-first für neue Besucher (Akquise): nur ein explizites ?tab=login zeigt zuerst
  // die Anmeldung. Alle Deep-Link-Redirects ohne Tab landen im verkaufenden Register-Modus.
  const [mode, setMode] = useState(() => params.get("tab") === "login" ? "login" : "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  // „Angemeldet bleiben" — vorangehakt: Kunden schauen sporadisch rein und sollen
  // nicht jedes Mal neu anmelden müssen (30-Tage-Session; abwählbar → 12 h).
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // AB-02: Register-Button-Copy-Test
  const ab02Variant = useRef("A");
  useEffect(() => {
    ab02Variant.current = getVariant("AB-02", { trackFn: trackEvent });
  }, []);

  const isRegister = mode === "register";
  const isForgot = mode === "forgot";
  const [info, setInfo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");

    const mail = email.trim().toLowerCase();

    // Passwort vergessen: nur E-Mail nötig, neutrale Antwort (keine Konto-Enumeration).
    if (isForgot) {
      if (!mail) { setError(t("Bitte gib deine E-Mail-Adresse ein.", "Please enter your email address.")); return; }
      setLoading(true);
      try {
        const res = await requestPasswordReset(mail);
        setInfo(res?.message || t(
          "Wenn ein Konto existiert, haben wir dir einen Link geschickt. Bitte prüfe dein Postfach.",
          "If an account exists, we've sent you a link. Please check your inbox.",
        ));
        trackEvent("password_reset_requested_ui", {});
      } catch (_) {
        setInfo(t(
          "Wenn ein Konto existiert, haben wir dir einen Link geschickt. Bitte prüfe dein Postfach.",
          "If an account exists, we've sent you a link. Please check your inbox.",
        ));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!mail || !password) {
      setError(t("Bitte E-Mail und Passwort eingeben.", "Please enter your email and password."));
      return;
    }
    if (isRegister && password.length < 8) {
      setError(t("Das Passwort muss mindestens 8 Zeichen lang sein.", "The password must be at least 8 characters long."));
      return;
    }

    setLoading(true);
    try {
      if (isRegister) {
        await register(mail, password, workspaceName);
        toast.success(t("Konto erstellt — willkommen!", "Account created — welcome!"));
        trackEvent("register_success", {
          ab02_variant: ab02Variant.current,
          referrer: document.referrer || "",
          utm_source: params.get("utm_source") || "",
          utm_campaign: params.get("utm_campaign") || "",
        });
        // Neukunde: Onboarding soll greifen → Cache zurücksetzen
        try { localStorage.removeItem("ff_onboarding_done"); } catch (_) {}
        // 14-Tage-Testphase startet mit dem Konto (Server setzt trial_ends_at;
        // hier die UI-Uhr für Feature-Gates + Trial-Banner)
        startTrialIfAbsent();
        // Kaufabsicht fortsetzen: direkt in den Checkout (Stripe bzw. ehrlicher
        // Vormerk-Pfad). Nur wenn selectPlan tatsächlich weiterleitet, hier NICHT
        // zusätzlich navigieren — sonst bliebe der Kunde ohne Redirect auf /login hängen.
        if (intendedPlan) {
          const redirected = await selectPlan(intendedPlan, { billingPeriod: params.get("period") || undefined });
          if (redirected) return;
        }
      } else {
        await login(mail, password, remember);
        toast.success(t("Willkommen zurück!", "Welcome back!"));
        trackEvent("user_login", { referrer: document.referrer || "" });
        if (intendedPlan) {
          const redirected = await selectPlan(intendedPlan, { billingPeriod: params.get("period") || undefined });
          if (redirected) return;
        }
      }
      navigate(next, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (!err?.response) {
        // Netzwerkfehler: die Antwort hat den Browser nie erreicht (Funkloch,
        // Firmen-/Hotel-WLAN, UDP-Blocker). Bei der Registrierung kann das Konto
        // serverseitig trotzdem angelegt sein — der Nutzer darf das nicht als
        // "Registrierung fehlgeschlagen" lesen und in einer Sackgasse landen.
        setError(isRegister
          ? t(
              "Verbindungsproblem — die Antwort des Servers ist nicht angekommen. " +
              "Falls du eine Willkommens-E-Mail erhältst, wurde dein Konto bereits erstellt: " +
              "Wechsle dann oben zu „Anmelden“ und logge dich mit deinen Daten ein.",
              "Connection problem — the server's response never arrived. If you receive a " +
              "welcome email, your account was already created: switch to \"Log in\" above " +
              "and sign in with your details.",
            )
          : t(
              "Verbindungsproblem — der Server war nicht erreichbar. Bitte prüfe deine " +
              "Internetverbindung (ggf. anderes WLAN oder Mobilfunk) und versuche es erneut.",
              "Connection problem — the server couldn't be reached. Please check your " +
              "internet connection (try another network) and try again.",
            ));
      } else if (status === 401) {
        setError(t("E-Mail oder Passwort ist nicht korrekt.", "Email or password is incorrect."));
      } else if (status === 409 && isRegister) {
        // Konto existiert schon → direkt zum Anmelden-Tab wechseln statt Sackgasse.
        setMode("login");
        setError(typeof detail === "string" ? detail : t(
          "Mit dieser E-Mail existiert bereits ein Konto. Bitte melde dich an.",
          "An account with this email already exists. Please log in.",
        ));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("Es ist ein Fehler aufgetreten. Bitte versuche es erneut.", "Something went wrong. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-stone-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      {/* Brand */}
      <div className="flex items-center gap-3 mb-8">
        <img src="/images/logo.png" alt="Förderfuchs" width="240" height="276" className="h-12 w-auto" />
        <div className="leading-tight">
          <div className="font-heading font-extrabold text-xl text-stone-900">
            {t("KI Social Media Manager", "AI Social Media Manager")}
          </div>
          <div className="text-xs text-stone-500 tracking-wide">by Förderfuchs</div>
        </div>
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200 shadow-sm p-7">
        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1 p-1 bg-stone-100 rounded-xl mb-6">
          {[
            { key: "login", label: t("Anmelden", "Log in") },
            { key: "register", label: t("Kostenlos starten", "Start for free") },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-testid={`auth-tab-${tab.key}`}
              onClick={() => { setMode(tab.key); setError(""); }}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${
                mode === tab.key ? "bg-white text-stone-800 shadow-sm" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <h1 className="font-heading font-bold text-lg text-stone-800 mb-1">
          {isForgot ? t("Passwort zurücksetzen", "Reset password")
            : isRegister ? t("Kostenlos starten", "Start for free")
            : t("Willkommen zurück", "Welcome back")}
        </h1>
        <p className="text-sm text-stone-500 mb-5">
          {isForgot
            ? t(
                "Gib deine E-Mail-Adresse ein — wir schicken dir einen Link zum Zurücksetzen (60 Minuten gültig).",
                "Enter your email address — we'll send you a reset link (valid for 60 minutes).",
              )
            : isRegister
            ? t(
                "Starte direkt — Förderfuchs erstellt deinen ersten Monatsplan in 2 Minuten. 14 Tage kostenlos, ohne Kreditkarte.",
                "Get started right away — Förderfuchs creates your first monthly plan in 2 minutes. 14 days free, no credit card.",
              )
            : t(
                "Melde dich an, um deine Kanäle und deinen Content zu verwalten.",
                "Log in to manage your channels and your content.",
              )}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <Field
              icon={Building2}
              label={t("Firmen-/Workspace-Name (optional)", "Company / workspace name (optional)")}
              type="text"
              value={workspaceName}
              onChange={setWorkspaceName}
              placeholder={t("z. B. Meine Firma", "e.g. My Company")}
              testid="auth-workspace"
            />
          )}
          <Field
            icon={Mail}
            label={t("E-Mail", "Email")}
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="name@company.com"
            autoComplete="email"
            testid="auth-email"
            required
          />
          {!isForgot && (
            <Field
              icon={Lock}
              label={t("Passwort", "Password")}
              type="password"
              value={password}
              onChange={setPassword}
              placeholder={isRegister ? t("Mind. 8 Zeichen", "At least 8 characters") : "••••••••"}
              autoComplete={isRegister ? "new-password" : "current-password"}
              testid="auth-password"
              required
            />
          )}

          {/* Angemeldet bleiben + Passwort vergessen — nur im Login-Modus */}
          {mode === "login" && (
            <div className="flex items-center justify-between -mt-1">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-600 select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="auth-remember"
                  className="w-3.5 h-3.5 accent-orange-500 rounded"
                />
                {t("Angemeldet bleiben", "Stay logged in")}
              </label>
              <button
                type="button"
                data-testid="auth-forgot-link"
                onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                className="text-xs text-stone-500 hover:text-fox-fire hover:underline"
              >
                {t("Passwort vergessen?", "Forgot password?")}
              </button>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">
              {error}
            </div>
          )}
          {info && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2" data-testid="auth-info">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="w-full flex items-center justify-center gap-2 bg-fox-fire hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-all disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
            {isForgot ? t("Link zum Zurücksetzen senden", "Send reset link")
              : isRegister
              ? (ab02Variant.current === "B" ? t("Kostenlosen Account erstellen", "Create free account") : t("14 Tage kostenlos starten", "Start free for 14 days"))
              : t("Anmelden", "Log in")}
          </button>
        </form>

        <p className="text-center text-sm text-stone-500 mt-5">
          {isForgot ? (
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); setInfo(""); }}
              className="text-fox-fire font-medium hover:underline"
            >
              {t("Zurück zur Anmeldung", "Back to login")}
            </button>
          ) : (
            <>
              {isRegister ? t("Schon ein Konto?", "Already have an account?") : t("Noch kein Konto?", "Don't have an account yet?")}{" "}
              <button
                type="button"
                onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); setInfo(""); }}
                className="text-fox-fire font-medium hover:underline"
              >
                {isRegister ? t("Jetzt anmelden", "Log in now") : t("Kostenlos starten", "Start for free")}
              </button>
            </>
          )}
        </p>
      </div>

      {/* Vertrauens-/DSGVO-Hebel — direkt auf der Akquise-Oberfläche sichtbar */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-stone-500">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={14} className="text-fox-fire" /> {t("DSGVO-konform", "GDPR-compliant")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Server size={14} className="text-fox-fire" /> {t("Daten in der EU", "Data hosted in the EU")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Lock size={14} className="text-fox-fire" /> {t("Verschlüsselte Tokens", "Encrypted tokens")}
        </span>
        <Link to="/vertrauen" className="text-fox-fire font-medium hover:underline">
          {t("Mehr erfahren", "Learn more")}
        </Link>
      </div>

      <p className="text-xs text-stone-400 mt-4 text-center max-w-md">
        {t("Mit der Anmeldung stimmst du den", "By logging in, you agree to the")}{" "}
        <Link to="/nutzungsbedingungen" className="underline hover:text-stone-600">{t("Nutzungsbedingungen", "Terms of Service")}</Link>{" "}
        {t("zu. Informationen zum Datenschutz findest du in der", "Information on data protection can be found in the")}{" "}
        <Link to="/datenschutz" className="underline hover:text-stone-600">{t("Datenschutzerklärung", "Privacy Policy")}</Link>.
      </p>
    </div>
  );
}

function Field({ icon: Icon, label, type, value, onChange, placeholder, autoComplete, required, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-stone-700">{label}</span>
      <div className="mt-1 relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          data-testid={testid}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-stone-200 focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none text-sm"
        />
      </div>
    </label>
  );
}
